import {
    BlurEvent, ChangeEvent, Component, DataDisplayEvent, DataSourceAction, DataSourceExecutionEvent, DropdownItem,
    DropdownSelectionEvent, Label, Layout, TableContentsChangedEvent, TableRow, TableRowCreationEvent,
    TableRowDisplayEvent, TableRowMode, Textbox, ValidationResult
} from "@mcleod/components";
import { Api, Currency, CurrencyUtil, ModelRow, StringUtil } from "@mcleod/core";
import { LMEUserSettings } from "@mcleod/general";
import { AutogenLayoutExtraPay } from "./autogen/AutogenLayoutExtraPay";
import { CommonDialogs } from "@mcleod/common";

export class ExtraPay extends AutogenLayoutExtraPay {
    private _doAfterClose: (canceled: boolean) => void;
    private totalExtraPay: number = 0.00;
    private _overridePayRateValidationMsg: string = "Please enter a number that is at most 10 digits in length, with at most 2 decimal places.";
    private driverIds: string[];
    private _endDate: Date;
    public onSave = (): Promise<boolean> => this.updateExtraPay();


    private async updateExtraPay(): Promise<boolean> {
        this.tableExtraPay.persistChangesImmediately = true;
        
        this.tableExtraPay.rows?.forEach(tableRow => {
            tableRow.saveChanges();
        })
        this.tableExtraPay.persistChangesImmediately = false;

        this.sourceExtraPay.deletedData?.forEach(modelRow => {
            modelRow.delete();
        })

        await this.sourceMovement.post();

        this.close(false);
        return true;
    }

    close(canceled: boolean) {
        this.slideOut().then(() => {
            if (this.doAfterClose != null)
                this.doAfterClose(canceled);
        });
    }

    public set doAfterClose(f) {
        this._doAfterClose = f;
    }

    public get doAfterClose() {
        return this._doAfterClose;
    }

    /** This is an event handler for the onContentsChanged event of tableExtraPay.  */
    tableExtraPayOnContentsChanged(event: TableContentsChangedEvent) {
        this.calculatePay();
    }

    tableExtraPayOnRowCreate(event: TableRowCreationEvent) {
        const modelRow = event.getTableRow().data as ModelRow;
        if (modelRow._appending) {
            modelRow.set("order_id", this.sourceMovement.activeRow?.get("orders.id"));
            modelRow.set("movement_id", this.sourceMovement.activeRow?.get("id"));
            modelRow.set("entered_user_id", LMEUserSettings.getUserId());
        }

        this.calculatePay();
    }

    tableExtraPayOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const deductionType = tableRow.findComponentById("textboxDeductionType") as Textbox;
        const codeType = tableRow.data.get("deduct_code.code_type", null);
        if (codeType != null)
            deductionType.text = this.getCodeTypeDisplayValue(codeType);
        if(tableRow.mode === TableRowMode.QUICK_ADD) {
            const textBoxDateToPay = tableRow.findComponentById("textboxDateToPay") as Textbox;
            textBoxDateToPay.defaultDataValue = this._endDate?.toString();

            if(this.driverIds.length === 1) {
                const textboxDriverId = tableRow.findComponentById("textboxDriverId") as Textbox;
                Api.search(textboxDriverId.lookupModel, { id: this.driverIds[0] }).then(response => {
                    if (response?.data[0] != null) {
                        const quickAddData = this.tableExtraPay.quickAddData as ModelRow;
                        const lmModelRow = textboxDriverId.createLookupModelRow({
                            id: response.data[0].id,
                            name: response.data[0].name,
                            first_name: response.data[0].first_name
                        });
                        quickAddData.setValueAndLookupModelData("driver_id", response.data[0].id, lmModelRow);
                        textboxDriverId.displayData(quickAddData, null, 0);
                    }
                });
            }

        }

        const textboxAmount = event.getTableRow()?.findComponentById("textboxAmount") as Textbox;
        textboxAmount.quickInfoOverrideKeyValue = () => {
            const row = TableRow.getContainingTableRow(event.target as Component).data;
            if (row.get("fuel_surcharge_pay") != null) {
                return row.get("id");
            }
            return null;
        };

        const textboxDeductCodeId = event.getTableRow()?.findComponentById("textboxDeductCodeId") as Textbox;
        textboxDeductCodeId.quickInfoOverrideSearchMethod = async (layout: Layout, keyValue: any) => {
            if (!StringUtil.isEmptyString(keyValue)) {
                const row = TableRow.getContainingTableRow(event.target as Component).data;
                await layout.mainDataSource.search({ search: keyValue })
                layout["textboxDriveExtraPayId"].text = keyValue; //deduct_code_id
                layout["textboxDeductDescr"].text = row.get("short_desc");
            }
        };
    }

    private calculatePay() {
        let symbol = "$";
        this.totalExtraPay = 0.00;
        this.tableExtraPay.rows.forEach(tableRow => {
            if (tableRow.data?.isNull("amount")) return;

            const codeType = tableRow.data?.get("deduct_code.code_type");
            const deductionCodeType = tableRow.data?.get("deduction_code_type");

            if (codeType === "E" || codeType === "R" ||  deductionCodeType === "Earning" || deductionCodeType === "Reimbursement") {
                this.totalExtraPay += tableRow.data?.get("amount").amount;
            } else {
                this.totalExtraPay -= tableRow.data?.get("amount").amount;
            }
            symbol = tableRow.data?.get("amount").symbol;
        });
        this.labelTotalExtraPayAmount.text = symbol + this.totalExtraPay.toFixed(2);
    }

    textboxDeductCodeIdOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        if (event.userInitiatedChange && event.newValue != event.oldValue) {
            const tableRow = TableRow.getContainingTableRow(textbox);
            const textboxShortDesc = tableRow.findComponentById("textboxShortDesc") as Textbox;
            const textboxDeductionType = tableRow.findComponentById("textboxDeductionType") as Textbox;
            const selectingFromDropdown = textbox.isDropdownVisible() && textbox.getFirstLookupModelData() != null;
            if (selectingFromDropdown) {
                // after selecting a deduct code, set the textboxShortDesc.text and then textboxUnits gets focus
                textboxShortDesc.text = textbox.getFirstLookupModelData().get("short_desc");
                textboxDeductionType.text = this.getCodeTypeDisplayValue(textbox.getFirstLookupModelData().get("code_type"));
                textbox.hideDropdown(false);
                tableRow.findComponentById("textboxUnits").focus();
            } else {
                textboxShortDesc.text = null;
            }
        }
    }

    textboxOverrideTypeOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            const overrideType: string = (event.target as Textbox).getDataValue();
            this.handleTextboxOverrideTypeChange(overrideType);
        }
    }

    textboxOverrideTypeOnSelect(event: DropdownSelectionEvent) {
        const overrideType: string = event.selection?.value;
        this.handleTextboxOverrideTypeChange(overrideType);
    }

    private handleTextboxOverrideTypeChange(overrideType: string) {
        if (overrideType === "D") {
            const moveDistance: number = this.sourceMovement.activeRow?.get("move_distance");
            this.sourceMovement.activeRow?.set("override_units", moveDistance);
        } else if (overrideType === "F") {
            this.sourceMovement.activeRow?.set("override_units", 1);
        } else {
            this.sourceMovement.activeRow?.set("override_units", null);
        }
        this.calculateOverridePayAmount();
    }

    private getCodeTypeDisplayValue(codeType: string): string {
        switch (codeType) {
            case "R": {
                return "Reimbursement";
            }
            case "E": {
                return "Earning";
            }
            case "D": {
                return "After-tax Deduction";
            }
            case "B": {
                return "Before-tax Deduction";
            }
            case "P": {
                return "Per Diem";
            }
            default: {
                return "";
            }
        }
    }

    textboxDeductCodeIdBeforeLookupModelSearch(event) {
        event.filter.code_type = "in R,E,D,B";
        event.filter.dispatcher_flag = 'Y';
    }

    textboxUnitsOnBlur(event: BlurEvent) {
        this.payAdjustmentsTextboxOnBlur(event);
    }

    textboxRateOnBlur(event: BlurEvent) {
        this.payAdjustmentsTextboxOnBlur(event);
    }

    payAdjustmentsTextboxOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            const textbox: Textbox = event.target as Textbox;
            this.payAdjustmentsTextboxChanged(textbox);
        }
    }

    payAdjustmentsTextboxChanged(textbox: Textbox) {
        if (textbox.text != null && textbox.text != "") {
            Api.search("lme/dispatch/calculate-carrier-other-pay",
                {
                    "other_pay_row": TableRow.getContainingTableRow(textbox).data //,
                }).then(response => {
                this.payAdjustmentsFromApi(response, textbox);
            });
        } else {
            const tableRow: TableRow = TableRow.getContainingTableRow(textbox);
            const descTextbox: Textbox = tableRow.findComponentById("textboxShortDesc") as Textbox;
            tableRow.data.set("short_desc", "");
            descTextbox.displayData(tableRow.data, null, 0);
        }
    }

    private payAdjustmentsFromApi(response, textbox: Textbox) {
        const data = response.data[0];
        const payAdjustments = data["other_pay_row"];
        const row = TableRow.getContainingTableRow(textbox);

        const descTextbox: Textbox = row.findComponentById("textboxShortDesc") as Textbox;
        if (payAdjustments["short_desc"] != null)
            descTextbox.text = payAdjustments["short_desc"];

        const units: Textbox = row.findComponentById("textboxUnits") as Textbox;
        units.text = payAdjustments["units"]?.toString();
        const rate: Textbox = row.findComponentById("textboxRate") as Textbox;
        rate.text = payAdjustments["rate"]?.toString();
        TableRow.getContainingTableRow(textbox).data.set("amount", payAdjustments["amount"]);

        const chargeRow: TableRow = TableRow.getContainingTableRow(textbox);
        chargeRow.data.set("amount", payAdjustments["amount"]);
        const amount: Textbox = row.findComponentById("textboxAmount") as Textbox;
        if (row.mode === TableRowMode.QUICK_ADD)
            amount.text = CurrencyUtil.formatCurrency(payAdjustments["amount"])?.toString();
    }

    textboxOverridePayeeIdBeforeLookupModelSearch(event) {
        event.filter["payee.status"] = 'A';
    }

    textboxOverridePayRateOnBlur() {
        const validRate: ValidationResult = this.validateOverridePayRate(false);
        if (validRate.isValid) {
            this.calculateOverridePayAmount();
        } else {
            this.textboxOverridePayRate.validationWarning = validRate.validationMessage;
        }
    }

    calculateOverridePayAmount() {
        const overrideUnitsString = this.sourceMovement.activeRow?.get("override_units");
        const overridePayRateString = this.sourceMovement.activeRow?.get("override_pay_rate");

        const overrideUnits: number = (typeof overrideUnitsString === "string") ? parseFloat(overrideUnitsString.replace(/,/g, "")) : overrideUnitsString;
        const overridePayRate: number = (typeof overridePayRateString === "string") ? parseFloat(overridePayRateString.replace(/,/g, "")) : overridePayRateString;

        if (overrideUnits && overridePayRate) {
            const result: number = overrideUnits * overridePayRate;
            if (result > Number.MAX_SAFE_INTEGER) {
                this.textboxOverridePayAmt.clear();
                this.sourceMovement.activeRow?.set("override_pay_amt", null);
                this.textboxOverridePayAmt.showAdhocTooltip("Calculated pay amount is too large.", { timeout: 5000 });

            } else {
                const overridePayAmt: Currency = new Currency({ amount: result });
                this.sourceMovement.activeRow?.set("override_pay_amt", overridePayAmt);
            }

        } else {
            this.sourceMovement.activeRow?.set("override_pay_amt", null);
        }
    }

    sourceMovementBeforeExecution(event: DataSourceExecutionEvent) {
        if (event?.getAction() === DataSourceAction.ADD || event.getAction() === DataSourceAction.UPDATE) {
            const validRate: ValidationResult = this.validateOverridePayRate(true);
            if (!validRate.isValid) {
                event.stop();
            }
        }
    }

    public validateOverridePayRate(preventSave: boolean): ValidationResult {
        if (this.mainDataSource.activeRow != null) {
            const rateString = String(this.textboxOverridePayRate.getDataValue());
            const overridePayRate: string = rateString.replace(/,/g, "")
            const decimalPos = overridePayRate.indexOf(".");
            const isNegative: boolean = (overridePayRate.charAt(0) === '-');

            if (overridePayRate != "" && isNaN(parseFloat(overridePayRate))) {
                this.showOverrideRateErrorMsg(preventSave);
                return {
                    component: this.textboxOverridePayRate,
                    isValid: false,
                    validationMessage: this.overridePayRateValidationMsg
                };
            }

            if (decimalPos >= 0) {
                const leftOfDecimal = (isNegative) ? overridePayRate.substring(1, decimalPos) : overridePayRate.substring(0, decimalPos);
                const rightOfDecimal = overridePayRate.substring(decimalPos + 1);

                // Prevent decimal rounding from affecting validation by truncating to two decimal places
                if (rightOfDecimal.length > 2) {
                    const truncatedRate = (isNegative) ? overridePayRate.substring(0, leftOfDecimal.length + 4) : overridePayRate.substring(0, leftOfDecimal.length + 3);
                    this.textboxOverridePayRate.text = truncatedRate;
                    this.mainDataSource.activeRow.set("override_pay_rate", truncatedRate);
                }

                if (leftOfDecimal.length > 8) {
                    this.showOverrideRateErrorMsg(preventSave);
                    return {
                        component: this.textboxOverridePayRate,
                        isValid: false,
                        validationMessage: this.overridePayRateValidationMsg
                    };
                }
            } else if (decimalPos === -1 && overridePayRate.length > 8) {
                this.showOverrideRateErrorMsg(preventSave);
                return {
                    component: this.textboxOverridePayRate,
                    isValid: false,
                    validationMessage: this.overridePayRateValidationMsg
                };
            }
        }

        return {
            component: this.textboxOverridePayRate,
            isValid: true,
            validationMessage: null
        };
    }

    showOverrideRateErrorMsg(preventSave: boolean) {
        if (preventSave) {
            CommonDialogs.showError("Invalid rate. " + this.overridePayRateValidationMsg, "Invalid Data");
            this.textboxOverridePayRate.focus();
        } else {
            this.textboxOverridePayRate.showAdhocTooltip(this.overridePayRateValidationMsg, { timeout: 5000 });
        }
        this.textboxOverridePayAmt.clear();
    }

    get overridePayRateValidationMsg(): string {
        return this._overridePayRateValidationMsg;
    }

    /** This is an event handler for the afterExecution event of sourceMovement. */
    sourceMovementAfterExecution(event: DataSourceExecutionEvent) {
        if (this.mainDataSource?.activeRow?.get("brokerage") != "Y") {
            this.textboxOverrideType.items = [new DropdownItem("D", "Distance"), new DropdownItem("F", "Flat rate")];
        }
    }

    public setDriverIds(driverIds: string[]) {
        this.driverIds = driverIds;
    }

    set endDate(endDate: Date) {
        this._endDate = endDate;
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxDriverId. */
    textboxDriverIdBeforeLookupModelSearch(event) {
        if(!(this.driverIds.length === 0)) {
            if(this.driverIds.length === 1) 
                event.filter.id = this.driverIds[0];
            else    
                event.filter.id = this.driverIds[0] + " || " + this.driverIds[1];
        }
    }
}
